import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, computed, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { EntityCostReport } from '../../model/skucost/entity-cost-report';
import { EntityLine } from '../../model/skucost/entity-line';
import { EntityLineSku } from '../../model/skucost/entity-line-sku';
import { EntityLinePart } from '../../model/skucost/entity-line-part';
import { CostMappingStatus, CostMappingUtils } from '../../utils/cost-mapping-utils';
import { SkuCostService } from '../../services/sku-cost.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { StringUtils } from '../../utils/string-utils';
import { FileUtils } from '../../utils/file-utils';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription } from '@angular/material/expansion';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { CurrencyPipe } from '@angular/common';
import { Entity } from '../../entity/entity';
import { EntityService } from '../../services/entity-service';
import { PaperPilotService } from '../../services/paper-pilot.service';
import { PersonLoginService } from '../../services/person-login-service';
import { MatTooltip } from '@angular/material/tooltip';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from "rxjs";
import { UnrestrictedEntityUpdate } from "../../model/unrestricted-entity-update";

@Component({
  selector: 'app-entity-cost-report',
  templateUrl: './entity-cost-report.component.html',
  styleUrls: ['./entity-cost-report.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatIcon,
    FormsModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatProgressSpinner,
    MatTooltip,
    CurrencyPipe,
  ],
})
export class EntityCostReportComponent implements OnInit, OnChanges {
  private readonly service = inject(SkuCostService);
  private readonly entityService = inject(EntityService);
  private readonly paperPilotService = inject(PaperPilotService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly router = inject(Router);
  private readonly entity = signal<Entity>(undefined!);
  entityCostReport!: EntityCostReport;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly entityId = input.required<string>();
  readonly entityRevision = input.required<string>();
  readonly revision = computed(() => +this.entityRevision());
  scenarioMultiplier!: number;

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.getCostReport();
    }
  }

  ngOnInit(): void {
    if (!this.entityId() || StringUtils.isEmptyNumericValue(this.revision())) {
      this.router.navigate(['/']).then(() => this.isBusy.set(false));
    } else {
      this.getCostReport();
    }
  }

  backToEntity(): void {
    if (!this.entityId() || StringUtils.isEmptyNumericValue(this.revision())) {
      this.router.navigate(['/']).then(() => this.isBusy.set(false));
    } else {
      const entityName: string = this.entityId().startsWith('QT') ? 'quotes' : 'orders';
      this.router.navigate([entityName, this.entityId(), this.revision()]).then(() => this.isBusy.set(false));
    }
  }

  getCostReport(): void {
    this.entityCostReport = undefined!;
    this.errorMessage.set(undefined!);
    if (this.entityId()?.trim()?.length) {
      this.isBusy.set(true);
      const entityName: string = this.entityId().startsWith('QT') ? 'QUOTE' : 'CONTRACT';
      this.entityService.getEntity(entityName, this.entityId(), this.revision()).subscribe({
        next: (entity: Entity) => {
          this.entity.set(entity);
          this.service.getCostReport(this.entityId(), this.revision()).subscribe({
            next: (report) => {
              this.entityCostReport = report;
              if (this.entityCostReport) {
                if ((this.entityCostReport.configuredEntityLines?.length || 0) > 0) {
                  this.entityCostReport.configuredEntityLines = this.entityCostReport.configuredEntityLines.sort((first, second) => +first.lineId - +second.lineId);
                  for (const line of this.entityCostReport.configuredEntityLines) {
                    line.skus = line.skus.sort((first, second) => first.sequence - second.sequence);
                  }
                }
                if ((this.entityCostReport.partAndRiserLines?.length || 0) > 0) {
                  this.entityCostReport.partAndRiserLines = this.entityCostReport.partAndRiserLines.sort((first, second) => +first.lineId - +second.lineId);
                }
              }
            },
            error: (error: HttpErrorResponse) => this.handleError(error),
            complete: () => (this.isBusy.set(false)),
          });
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
    }
  }

  exportCostReport(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.service.exportCostReport(this.entityId(), this.revision()).subscribe({
      next: (file: Blob) => FileUtils.downloadFile(file, this.entityId() + '-CostReport.xlsx'),
      error: (error: HttpErrorResponse) => this.handleError(error),
      complete: () => this.isBusy.set(false),
    });
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  getListAmount(): number {
    if (this.entityCostReport && this.entityCostReport.multiplier) {
      return this.entityCostReport.equipmentNetAmount / this.entityCostReport.multiplier;
    }
    return 0;
  }

  calculateScenarioEquipment(): number {
    if (!isNaN(this.scenarioMultiplier)) {
      return +(this.getListAmount() * this.scenarioMultiplier).toFixed(2);
    }
    return 0;
  }

  calculateScenarioTotal(): number {
    if (!isNaN(this.scenarioMultiplier)) {
      return this.calculateScenarioEquipment();
    }
    return 0;
  }

  calculateScenarioTotalWithShipping(): number {
    if (isNaN(this.scenarioMultiplier)) {
      return 0;
    } else {
      return this.calculateScenarioTotal() + this.entityCostReport.shippingChargeAmount;
    }
  }

  calculateExpectedMarginAmount(): number {
    if (!isNaN(this.scenarioMultiplier)) {
      const list = this.getListAmount();
      if (list === 0) {
        return 0;
      } else {
        const cost = this.getTotalCost();
        const scenarioTotal = this.calculateScenarioTotal();
        return scenarioTotal - cost;
      }
    }
    return 0;
  }

  calculateExpectedMarginPercentage(): string {
    if (!isNaN(this.scenarioMultiplier)) {
      const list = this.getListAmount();
      if (list === 0) {
        return '0';
      } else {
        const cost = this.getTotalCost();
        const scenarioTotal = this.calculateScenarioTotal();
        const margin = scenarioTotal - cost;
        return this.calculateMarginPercentage(margin, scenarioTotal);
      }
    }
    return '0';
  }

  calculateMarginPercentage(margin: number, scenarioTotal: number): string {
    return scenarioTotal == 0 ? '0' : ((100 * +margin) / scenarioTotal).toFixed(2) + '%';
  }

  getSfrLink(sfrNumber: string): string {
    const sfrId = sfrNumber.trim().replace('SFR', '');
    return environment.safariBaseUrl + 'request/view/' + sfrId;
  }

  getTotalCost(): number {
    const multiplier: number = this.entityCostReport.costFactor || 1;
    return multiplier * this.entityCostReport?.cost || 0;
  }

  getTotalMargin(): number {
    const revenue: number = this.entityCostReport?.totalRevenue || 0;
    const cost: number = this.getTotalCost();
    return revenue - cost;
  }

  saveCostFactor(): void {
    if (!this.isBusy() && this.entity()) {
      this.isBusy.set(true);
      this.updateCostFactor().then((updated: Entity) => {
        this.isBusy.set(false);
        this.entity.set(updated);
      }, (error: HttpErrorResponse) => this.handleError(error));
    }
  }

  private async updateCostFactor(): Promise<Entity> {
    const update: UnrestrictedEntityUpdate = new UnrestrictedEntityUpdate();
    update.entityId = this.entity().id.entityId;
    update.costFactor = +this.entityCostReport.costFactor;
    update.revision = this.entity().id.entityRevision;
    return await firstValueFrom<Entity>(this.entityService.saveEntityWithoutRestrictions(update));
  }

  isPaperPilotUser(): boolean {
    return this.personLoginService.isPaperPilotUser();
  }

  lineCostMappingStatus(line: EntityLine): CostMappingStatus {
    return this.isPaperPilotUser() ? CostMappingUtils.lineStatus(line) : 'ok';
  }

  skuCostMappingStatus(sku: EntityLineSku): CostMappingStatus {
    return this.isPaperPilotUser() ? CostMappingUtils.skuStatus(sku) : 'ok';
  }

  partCostMappingStatus(part: EntityLinePart): CostMappingStatus {
    return this.isPaperPilotUser() ? CostMappingUtils.partStatus(part) : 'ok';
  }

  lineCostMappingTooltip(line: EntityLine): string {
    return CostMappingUtils.lineTooltip(this.lineCostMappingStatus(line));
  }

  skuCostMappingTooltip(sku: EntityLineSku): string {
    return CostMappingUtils.skuTooltip(sku);
  }

  partCostMappingTooltip(): string {
    return CostMappingUtils.partTooltip();
  }

  costMappingIconClass(status: CostMappingStatus): string {
    return CostMappingUtils.iconClass(status);
  }

  canCreatePaperPilot(): boolean {
    if (!this.entityId().startsWith('QT') || !this.entityCostReport) {
      return false;
    }
    return (this.entityCostReport.configuredEntityLines || []).some((line) => this.lineCostMappingStatus(line) === 'paperPilot');
  }

  createPaperPilot(): void {
    this.paperPilotService.create(this.entity().id, this.isBusy, (error) => this.handleError(error));
  }

  costMappingRowClass(status: CostMappingStatus): string {
    return this.isPaperPilotUser() ? CostMappingUtils.rowClass(status) : '';
  }

}
